<!-- No observation on this sensor means the task has no trigger, and a section
     explaining an absent feature is noise — absent rather than empty. -->
<div class="ais-obs" *ngIf="views.length">

  <!-- A div, not a <button>: the body carries its own buttons and nesting them
       is invalid HTML — the same reason its four siblings use one. -->
  <div class="ais-obs__head"
       role="button"
       tabindex="0"
       [class.is-hit]="isHit"
       [attr.aria-expanded]="expanded"
       (click)="toggle()"
       (keydown.enter)="toggle()"
       (keydown.space)="$event.preventDefault(); toggle()">
    <mat-icon class="material-icons-outlined ais-obs__icon">crisis_alert</mat-icon>
    <span class="ais-obs__title">Observations</span>

    <!-- THE OBSERVATION ITSELF, in the slot every sibling spends on a number.
         What the task is watching for is the one fact that has to survive the
         section being collapsed. -->
    <span class="ais-obs__watch" *ngIf="headerChip" [title]="headerChip.valueTitle">
      <i>{{ headerChip.kindLabel }}</i>
      <b>{{ headerChip.valueLabel }}</b>
    </span>

    <span class="ais-obs__spacer"></span>
    <span class="ais-obs__count">{{ headerCountLabel }}</span>

    <!-- Which section owns the map. Its own control rather than the header's
         click, which stays expand/collapse: a task is routinely read with this
         section folded shut and its vessels drawn, and the two states have to be
         settable apart. Turns itself off when the snapshot archive takes the
         map — the host owns that arbitration and pushes the answer back in. -->
    <button type="button" class="ais-obs__onmap"
            [class.is-on]="onMap"
            [attr.aria-pressed]="onMap"
            [matTooltip]="onMapTooltip"
            matTooltipPosition="below" matTooltipClass="vr-tip-below"
            (click)="toggleMap($event)">
      <mat-icon class="material-icons-outlined">{{ onMap ? 'layers' : 'layers_clear' }}</mat-icon>
    </button>

    <!-- Frame the observed area. Only a location observation has one, and the
         host draws it with the sensor rather than on a click, so the one act
         left is a look at it — there is no "off" for a shape that is a property
         of the task. -->
    <button type="button" class="ais-obs__centre"
            *ngIf="areaView"
            matTooltip="Centre the map on the observed area"
            matTooltipPosition="below" matTooltipClass="vr-tip-below"
            (click)="centre($event)">
      <mat-icon class="material-icons-outlined">my_location</mat-icon>
    </button>

    <mat-icon class="ais-obs__caret">{{ expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
  </div>

  <ng-container *ngIf="expanded">
    <div class="ais-obs__block"
         *ngFor="let block of blocks; trackBy: trackByObservation">

      <!-- Only when the header chip cannot speak for a single observation. -->
      <div class="ais-obs__sub" *ngIf="!headerChip" [title]="block.view.valueTitle">
        <i>{{ block.view.kindLabel }}</i><b>{{ block.view.valueLabel }}</b>
      </div>

      <!-- Above the rows, not under them: it says what the task is watching for
           and is read before the list, where under it reads as a caption on
           something already understood. -->
      <div class="ais-obs__hint">
        {{ block.view.meaning }}
        <ng-container *ngIf="block.matches.length && snapshotLabel">
          Seen in the <b>{{ snapshotLabel }}</b> snapshot.
        </ng-container>
      </div>

      <!-- ── Nothing is drawn, so nothing has been searched ─────────────────
           This is the state the old checkbox spent disabled with nothing
           saying why. -->
      <div class="ais-obs__none" *ngIf="block.empty === 'idle'">
        <mat-icon class="material-icons-outlined">layers</mat-icon>
        <div>
          <div class="ais-obs__none-title">Nothing to search yet</div>
          <div class="ais-obs__none-note">
            Put a snapshot on the map — press Refresh, or pick one from
            <a href="#" (click)="browse($event)">Refresh History</a> — and any vessel
            matching this shows up here.
          </div>
        </div>
      </div>

      <!-- ── The window closed and nothing is drawn ─────────────────────────-->
      <div class="ais-obs__none" *ngIf="block.empty === 'closed'">
        <mat-icon class="material-icons-outlined">event_busy</mat-icon>
        <div>
          <div class="ais-obs__none-title">
            Window closed<ng-container *ngIf="closeLabel"> {{ closeLabel }}</ng-container>
          </div>
          <div class="ais-obs__none-note">
            Nothing new will be collected. Snapshots already taken are still in
            <a href="#" (click)="browse($event)">Refresh History</a>, and this list
            searches whichever one is on the map.
          </div>
        </div>
      </div>

      <!-- ── A snapshot IS drawn and this hull is not in it ─────────────────
           Where the old path raised a blocking dialog. Says what was searched,
           so the answer is checkable, and says the task keeps watching — this
           list is about the snapshot on screen, never about the tip. -->
      <div class="ais-obs__none is-warn" *ngIf="block.empty === 'nomatch'">
        <mat-icon class="material-icons-outlined">search_off</mat-icon>
        <div>
          <div class="ais-obs__none-title">Not in this snapshot</div>
          <div class="ais-obs__none-note">
            Checked all {{ searchedCount }}
            {{ searchedCount === 1 ? 'vessel' : 'vessels' }}<ng-container *ngIf="snapshotLabel">
              in the <b>{{ snapshotLabel }}</b> snapshot</ng-container>. Try
            <a href="#" (click)="browse($event)">another snapshot</a>, or refresh for a
            newer one — the task keeps watching either way.
          </div>
        </div>
      </div>

      <!-- ── What was found ────────────────────────────────────────────────-->
      <div class="ais-obs__list" *ngIf="block.matches.length"
           role="listbox" [attr.aria-label]="'Vessels matching ' + block.view.kindLabel">
        <div class="ais-obs__row"
             *ngFor="let match of block.matches; trackBy: trackByMatch"
             role="option"
             tabindex="0"
             [class.is-active]="isActive(match)"
             [attr.aria-selected]="isActive(match)"
             matTooltip="Centre the map on this vessel"
             matTooltipPosition="below" matTooltipClass="vr-tip-below"
             (click)="pick(block, match)"
             (keydown.enter)="pick(block, match)"
             (keydown.space)="$event.preventDefault(); pick(block, match)">
          <span class="ais-obs__dot"></span>
          <span class="ais-obs__text">
            <span class="ais-obs__name" [class.is-unnamed]="!match.isNamed">{{ match.label }}</span>
            <small>
              <ng-container *ngIf="match.isNamed && match.mmsi">MMSI {{ match.mmsi }}</ng-container>
              <ng-container *ngIf="match.shipType"> · {{ match.shipType }}</ng-container>
              <ng-container *ngIf="match.speed"> · {{ match.speed }}</ng-container>
              <ng-container *ngIf="match.time"> · {{ match.time }}</ng-container>
            </small>
          </span>
          <span class="ais-obs__sel" *ngIf="isActive(match)">SELECTED</span>
          <span class="ais-obs__acts">
            <button type="button" class="ais-obs__act is-primary"
                    matTooltip="Centre the map on this vessel"
                    matTooltipPosition="below" matTooltipClass="vr-tip-below"
                    (click)="focusRow(block, match, $event)">
              <mat-icon class="material-icons-outlined">my_location</mat-icon>
            </button>
          </span>
        </div>
      </div>

    </div>
  </ng-container>

</div>
